import React, { useEffect, useMemo } from 'react';
import { AlertCircle, CheckCircle2, ArrowRight, Star, BookMarked } from 'lucide-react';
import { analyzeBreadIssues, generateMethodologyFeedback, generateImprovementSuggestions } from '../../lib/breadAnalysis';

interface FeedbackSectionProps {
  ratings: {
    crumbStructure: number;
    ovenSpring: number;
    crust: number;
    crumbTexture: number;
  };
  parameters: {
    fermentationType: string;
    fermentationDuration: number;
    bakingMethod: string;
    temperature: number;
    weight: number;
    bakingDuration: number;
    hydration: number;
  };
  onFeedbackGenerated?: (feedback: {
    mainIssue: string;
    methodology: string;
    improvements: string[];
  }) => void;
  onKnowledgeClick?: () => void;
}

export function FeedbackSection({ ratings, parameters, onFeedbackGenerated, onKnowledgeClick }: FeedbackSectionProps) {
  // Generate feedback only when inputs change
  const feedback = useMemo(() => {
    const analysis = analyzeBreadIssues({ ratings, parameters });
    const methodologyFeedback = generateMethodologyFeedback(parameters);
    const improvements = [
      ...(analysis.solutions || []), // Add solutions to improvements
      ...generateImprovementSuggestions(ratings, parameters)
    ];

    // Generate positive observations
    const positiveObservations: string[] = [];

    // Check ratings
    if (ratings.crumbStructure >= 80) {
      positiveObservations.push('Excellent crumb structure! Your fermentation and shaping techniques are working well.');
    }
    if (ratings.ovenSpring >= 80) {
      positiveObservations.push('Great oven spring! Your dough strength and steam management are on point.');
    }
    if (ratings.crust >= 80) {
      positiveObservations.push('Beautiful crust development! Your temperature and timing control is excellent.');
    }
    if (ratings.crumbTexture >= 80) {
      positiveObservations.push('Perfect crumb texture! Your baking process is well managed.');
    }

    // Check parameters
    if (parameters.temperature >= 230 && parameters.temperature <= 260) {
      positiveObservations.push('Your baking temperature is well calibrated for optimal crust development and even baking.');
    }
    if (parameters.fermentationType === 'direct' && parameters.fermentationDuration <= 8) {
      positiveObservations.push('Your fermentation time is well managed, allowing for proper flavor development without over-fermenting.');
    }

    return {
      analysis: {
        ...analysis,
        solutions: [] // Clear solutions as they're now part of improvements
      },
      methodologyFeedback,
      improvements,
      positiveObservations,
      forParent: {
        mainIssue: analysis.mainIssue,
        methodology: methodologyFeedback.join('\n'),
        improvements
      }
    };
  }, [
    ratings.crumbStructure,
    ratings.ovenSpring,
    ratings.crust,
    ratings.crumbTexture,
    parameters.fermentationType,
    parameters.fermentationDuration,
    parameters.bakingMethod,
    parameters.temperature,
    parameters.weight,
    parameters.bakingDuration,
    parameters.hydration
  ]);

  // Notify parent component of feedback changes
  useEffect(() => {
    if (onFeedbackGenerated && feedback.forParent) {
      const timeoutId = setTimeout(() => {
        onFeedbackGenerated(feedback.forParent);
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [feedback.forParent, onFeedbackGenerated]);

  return (
    <div className="space-y-6">
      {/* Positive Observations */}
      {feedback.positiveObservations.length > 0 && (
        <div className="p-4 bg-green-500/10 backdrop-blur-sm rounded-xl border border-green-500/20">
          <div className="flex items-start gap-2">
            <Star className="w-5 h-5 mt-0.5 text-green-400" />
            <div>
              <h3 className="font-medium text-green-200">Positive Observations</h3>
              <div className="mt-2 space-y-2">
                {feedback.positiveObservations.map((observation, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-1 flex-shrink-0 text-green-400" />
                    <p className="text-sm text-green-100/90">{observation}</p>
                  </div>
                ))}
              </div>
              <p className="mt-2 space-y-2 font-medium text-white italic">
                Well done!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Issue */}
      {feedback.analysis.mainIssue && (
        <div className="p-4 bg-amber-500/10 backdrop-blur-sm rounded-xl border border-amber-500/20">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 mt-0.5 text-amber-400" />
            <div>
              <h3 className="font-medium text-amber-200">Main Issue Identified</h3>
              <p className="mt-1 text-amber-100/90">{feedback.analysis.mainIssue}</p>
              {feedback.analysis.causes.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm text-amber-200 font-medium">Likely Causes:</p>
                  {feedback.analysis.causes.map((cause, index) => (
                    <p key={index} className="text-sm text-amber-100/80">• {cause}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Methodology Feedback */}
      {feedback.methodologyFeedback.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-medium text-white">Methodology Analysis</h3>
          <div className="space-y-2">
            {feedback.methodologyFeedback.map((feedbackItem, index) => (
              <div 
                key={index}
                className="p-3 bg-white/5 rounded-lg flex items-start gap-2"
              >
                <ArrowRight className="w-4 h-4 mt-1 flex-shrink-0 text-white/70" />
                <p className="text-sm text-white/80">{feedbackItem}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Improvements */}
      {feedback.improvements.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-medium text-white">Areas for potential refinement</h3>
          <div className="space-y-2">
            {feedback.improvements.map((improvement, index) => (
              <div 
                key={index}
                className="p-3 bg-white/5 rounded-lg flex items-start gap-2"
              >
                <CheckCircle2 className="w-4 h-4 mt-1 flex-shrink-0 text-green-400" />
                <p className="text-sm text-white/80">{improvement}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Success Message */}
      {!feedback.analysis.mainIssue && 
       feedback.methodologyFeedback.length === 0 && 
       feedback.improvements.length === 0 && (
        <div className="p-4 bg-green-500/10 backdrop-blur-sm rounded-xl border border-green-500/20">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 mt-0.5 text-green-400" />
            <div>
              <h3 className="font-medium text-green-200">Excellent Results!</h3>
              <p className="mt-1 text-green-100/90 text-sm">
                Your bread shows great caracteristics across all aspects. Keep up the great work!
              </p>
              <p className="font-medium text-green-200 mt-4">
                Want to go even further?
              </p>
              <p className="mt-1 text-green-100/90 text-sm">
                Try increasing your hydration by another 2-3% and see how your crumb evolves. If you haven't done so already, you can also try using ancient wheat varieties and explore their unique flavours.
              </p>
              <p className="mt-1 text-green-100/90 text-sm">  
                In anycase, make sure to join our Monthly Challenges and explore new horizons in the sourdough bread universe!
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}